/* eslint-disable react/jsx-one-expression-per-line, react/no-unstable-nested-components */
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import MuiDialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { AllDimensions, AllMappingDimensions, AllSeqObjectDimensions } from 'relevant-shared/mappingDimensions/allDimensionArrays';
import { createCsv } from 'relevant-shared/misc/misc';
import { Button } from '@mui/material';
import * as relevantApi from '../../api/relevant';
import DataGrid from '../DataGrid';
import DataTable from '../DataTable';
import OperationWrapper from '../OperationWrapper';
import { FormOf, Scope } from '../Wrappers';
import TextField from '../TextField';
import SingleMapping from './singleMapping';
import MatchReport from './matchReport';
import { ActionButton } from '../ActionButton/ActionButton';
import JobButton from '../JobButton';
import Select from '../Select';
import PopupSelector from '../PopupSelector';
import DynamicSearchFilter from '../DynamicSearchFilter';
import RecordTableTools from '../RecordTableTools';
import { stores } from '../../stores';
import Checkbox from '../Checkbox';
import CsvImport from '../CsvImport';
import Base from '../../layouts/Base';
import { createFilterFnOptions, filterOptions } from '../SiteSelect/filterOptions';
import BrowserUtils from '../../lib/browserUtils';
import DatePicker from '../DatePicker';
import DateUtils from '../../lib/dateUtils';
import SystemData from '../../lib/systemData';
import classes from '../../api/classes';
import { ConfirmDialog } from '../ConfirmDialog';
import { Dialog } from '../Dialog';
import { SmartHelp } from '../Smarts/SmartHelp';
import { ReactComponent as DownloadIcon } from '../../assets/icons/Download.svg?component';
import { Banner } from '../Banner';

const MAX_CROSS_COLS = 1;

const { Reports, SingleAdvertiserMapping } = classes;

const relevant = relevantApi.DynamicExports;
const seqDimsByName = _.keyBy(AllSeqObjectDimensions, 'name');
const allDimsByName = _.keyBy(AllDimensions, 'name');

const prompt = (text) => Base.renderGlobal((closeFn) => (
	<ConfirmDialog
		open
		text={text}
		onAny={closeFn}
		confirmLabel="Yes"
		cancelLabel="No"
	/>
));

class AdvertiserMappingEditor extends React.Component {
	constructor(props) {
		super(props);
		this.state = { mappingListFilter: '' };
		this.knownEntities = _.mapValues(seqDimsByName, () => ({}));
		this.mappingListRef = React.createRef();
	}

	async loadAdvertisers() {
		const { model } = this.props;
		const dim = this.mappingDim();
		const all = model.mappings.map((m) => [
			...[m.targetAdvNr, ...(m.advertisers || [])].map((nr) => ({ nr, name: dim.name })),
			..._.entries(m.crossDimMapping).map(([name, nr]) => ({ name, nr })),
		]).flat();
		const byName = _.mapValues(_.groupBy(all, 'name'), (arr) => _.uniq(_.map(arr, 'nr')));
		for (const [name, objNrs] of _.entries(byName)) {
			const { clsName } = seqDimsByName[name] || {};
			if (clsName) {
				const objects = await relevant[clsName].call('getObjects', { objNrs });
				this.knownEntities[name] = _.keyBy(objects.map((o) => ({ ...o, advNr: o.seq.toString() })), 'seq');
			}
		}
		this.updateMappingStructures();
	}

	advName(nr, type) {
		const { model: { dimType } } = this.props;
		const { sysSpecific, sysIdFld } = this.mappingDim();
		const entity = this.knownEntities[type || dimType]?.[nr];
		let name = entity?.name || '';
		if (name && sysSpecific) {
			const id = entity[sysIdFld];
			name += id ? ` (${id})` : '';
		}
		return name;
	}

	addAdvObjects(advOrAdvs) {
		const { model: { dimType } } = this.props;
		const advs = _.isArray(advOrAdvs) ? advOrAdvs : [advOrAdvs];
		advs.forEach((adv) => {
			adv.advNr = adv.seq;
			this.knownEntities[dimType][adv.advNr] = adv;
		});
	}

	async doImport({ nonExistingNames, rows, updateRowsFromAdvs, nonExistingCrossNames }) {
		const { model } = this.props;
		const { mappings } = model;
		const mappingDim = this.mappingDim();
		if (!rows.length) {
			return true; // nothing to do..
		}
		if (nonExistingNames.length || _.some(nonExistingCrossNames, (arr) => arr.length)) {
			const ok = await Base.renderGlobal((closeFn) => (
				<ConfirmDialog
					open
					text={(
						<span>
							We need to create&nbsp;
							<span style={{ fontWeight: 'bold' }}>
								{nonExistingNames.length}
							</span>
							{' '}
							new&nbsp;
							{mappingDim.name}
							(s)
							{' '}
							{_.map(nonExistingCrossNames, (arr, d) => (arr.length ? (
								<>
									and&nbsp;
									<span style={{ fontWeight: 'bold' }}>
										{arr.length}
									</span>
									{' '}
									new&nbsp;
									{d}(s)
									{' '}
								</>
							) : ''))}
							in the system, do you want to proceed?
						</span>
					)}
					onAny={closeFn}
				/>
			));
			if (!ok) {
				return false;
			}
			const { clsName } = this.mappingDim();
			const advs = nonExistingNames.length ? (await relevant[clsName].call('updateObjects', {
				objects: nonExistingNames.map((name) => ({ name })),
				returnObjects: true,
			})) : [];
			const toClsName = (dim) => allDimsByName[dim].clsName;
			const crossDimPromises = _.chain(nonExistingCrossNames)
				.entries()
				.filter(([, val]) => val.length)
				.map(async ([dim, val]) => [dim, await relevant[toClsName(dim)].call('updateObjects', {
					objects: val.map((name) => ({ name })),
					returnObjects: true,
				})])
				.value();
			const crossNames = _.fromPairs(await Promise.all(crossDimPromises));
			updateRowsFromAdvs(rows, advs, crossNames);
		}
		if (rows.find((r) => (r.fromAdv && !r.fromAdvNr) // let fromAdv === ""
			|| !r.toAdvNr
			|| _.some(mappingDim.crossMapTo, (d) => r[d] && !r[`${d}Nr`]))) {
			throw Error('Detected object without an assigned number');
		}
		const byTarget = _.keyBy(mappings, 'targetAdvNr');
		const byAdv = {};
		model.mappings.forEach((mapping) => {
			mapping.advertisers.forEach((advNr) => {
				byAdv[advNr] = mapping;
			});
		});
		let mappedCount = 0;
		rows.forEach((row) => {
			let mapping = byTarget[row.toAdvNr];
			if (!mapping) {
				mapping = {
					targetAdvNr: row.toAdvNr,
					advertisers: [row.toAdvNr],
					byWords: [],
					tempRandom: Math.random(),
				};
				byTarget[row.toAdvNr] = mapping;
				mappings.push(mapping);
			}
			if (row.fromAdvNr) {
				const oldMapping = byAdv[row.fromAdvNr];
				if (!oldMapping || oldMapping !== mapping) {
					mappedCount++;
				}
				if (oldMapping) {
					_.pull(oldMapping.advertisers, row.fromAdvNr);
				}
				mapping.advertisers.push(row.fromAdvNr);
			}
			if (_.some(mappingDim.crossMapTo, (d) => row[d])) {
				const newCrossMaps = mappingDim.crossMapTo.reduce((acc, d) => {
					const nr = row[`${d}Nr`];
					if (nr) acc[d] = nr;
					return acc;
				}, {});
				if (!_.isEqual(mapping.crossDimMapping, newCrossMaps)) {
					if (!row.fromAdvNr) { mappedCount++; } // If row.fromAdvNr didn't exist, we should still count this as a mapping change
					mapping.crossDimMapping = { ...newCrossMaps, ...mapping.crossDimMapping }; // Don't overwrite existing values
				}
			}
		});
		Base.renderGlobal((close) => (
			<Dialog
				open
				status="success"
				text={`Mapped ${mappedCount} ${this.mappingDim().pluralName}`}
				onClose={close}
			/>
		));
		this.updateMappingStructures();
		return true;
	}

	getVisibleMappings() {
		const { mappingListFilter: filter, mappingListFilterStr: str } = this.state;
		const { model } = this.props;
		let { mappings } = model;
		if (filter && str) {
			const include = _.keyBy(filterOptions(str, filter), 'id');
			mappings = mappings.filter((m) => include[m.targetAdvNr]);
		}
		return _.sortBy(mappings, (m) => this.advName(m.targetAdvNr));
	}

	async handleCsvImport(orgRows)	{
		const isAdvName = (name) => name && name !== '#N/A';
		const mappingDim = this.mappingDim();
		const splitWithEscape = (full, separator = ',') => {
			const parts = [];
			let current = '';
			for (let i = 0; i < full.length; i++) {
				const c = full[i];
				if (c === '\\') {
					if (i !== full.length - 1) {
						current += full[++i];
					}
				} else if (c === separator) {
					parts.push(current);
					current = '';
				} else {
					current += c;
				}
			}
			parts.push(current);
			return parts.map((s) => s.trim()).filter((s) => s);
		};

		const rows = _.chain(orgRows)
			.flatMap((row) => {
				const fromAdvs = splitWithEscape(row.fromAdv);
				// if there are no direct mappings but something else, don't skip it
				if (!fromAdvs.length && _.some(row, (v, k) => k !== 'toAdv' && v?.trim() !== '')) {
					fromAdvs.push('');
				}
				return fromAdvs.map((fromAdv) => {
					const newRow = { fromAdv, toAdv: row.toAdv.trim() };
					mappingDim.crossMapTo?.forEach((d) => {
						const name = row[d]?.trim();
						if (name) {
							newRow[d] = name;
						}
					});
					return newRow;
				});
			})
			.filter((row) => isAdvName(row.toAdv) && row.fromAdv !== row.toAdv)
			.uniqWith((val1, val2) => val1.fromAdv !== '' && val2.fromAdv !== '' && val1.fromAdv === val2.fromAdv) // don't filter if fromAdv is empty since there can be multiple unrelated rows that both lack fromAdv
			.value();

		// We may encounter situations where the mapping contains a non-trimmed name and the file being imported contains its trimmed version
		// Generate a warning about this.
		// We could use this.knownEntities for this, but that may not be fully accurate over edits (e.g. if the untrimmed version is deleted but not yet saved)
		const nonTrimmedMappedNames = new Set(this.props.model.mappings
			.flatMap(({ targetAdvNr, advertisers, crossDimMapping }) => [
				...[targetAdvNr, ...advertisers].map((nr) => [mappingDim.name, nr]),
				...Object.entries(crossDimMapping || {}),
			])
			.map(([dimName, nr]) => this.knownEntities[dimName][nr].name)
			.filter((name) => name !== name.trim())
			.map((name) => name.trim()));
		const trimProblemNames = _.chain(rows)
			.flatMap((row) => _.values(row))
			.uniq()
			.filter((name) => nonTrimmedMappedNames.has(name))
			.value();

		let nonExistingNames;
		let nonExistingCrossNames;
		let nonEmptyCrossDims = [];

		const updateRowsFromAdvs = (rowsArr, advs, crossDimObjs) => {
			const advsByName = _.keyBy(advs, 'name');
			const crossDims = _.mapValues(crossDimObjs, (v) => _.keyBy(v, 'name'));
			rowsArr.forEach((row) => {
				row.fromAdvNr = row.fromAdvNr || (advsByName[row.fromAdv] || {}).seq;
				row.toAdvNr = row.toAdvNr || (advsByName[row.toAdv] || {}).seq;
				_.forEach(crossDims, (dObj, d) => {
					row[`${d}Nr`] = row[`${d}Nr`] || (dObj[row[d]] || {}).seq;
				});
			});
			this.addAdvObjects(advs);
			_.forEach(crossDims, (dObj, d) => _.forEach(dObj, (obj) => {
				this.knownEntities[d][obj.seq] = { ...obj, advNr: obj.seq };
			}));
		};

		const loadAdvData = async () => {
			const { advNames, crossDimObjs } = _.chain(rows)
				.transform((acc, row) => {
					acc.advNames.push(row.fromAdv, row.toAdv);
					mappingDim.crossMapTo?.forEach((d) => row[d] && acc.crossDimObjs[d].push(row[d]));
				}, { advNames: [], crossDimObjs: _.fromPairs(mappingDim.crossMapTo?.map((d) => [d, []])) })
				.thru(({ advNames, crossDimObjs }) => ({
					advNames: _.uniq(advNames),
					crossDimObjs: _.mapValues(crossDimObjs, _.uniq),
				}))
				.value();
			const toClsName = (dim) => allDimsByName[dim].clsName;
			const existing = await relevant[mappingDim.clsName].call('getObjects', { objNames: advNames });
			const existingCrossDimsPromises = _.chain(crossDimObjs)
				.mapValues((val) => _.uniq(val))
				.entries()
				.filter(([, val]) => val.length)
				.map(async ([dim, val]) => [dim, await relevant[toClsName(dim)].call('getObjects', { objNames: val })])
				.value();
			const existingCrossDims = _.fromPairs(await Promise.all(existingCrossDimsPromises));
			updateRowsFromAdvs(rows, existing, existingCrossDims);
			const existingByName = _.keyBy(existing, 'name');
			nonExistingNames = advNames.filter((n) => n && !existingByName[n]);
			nonExistingCrossNames = _.mapValues(crossDimObjs, (arr, d) => {
				const byName = _.keyBy(existingCrossDims[d], 'name');
				return _.uniq(arr.filter((n) => !byName[n]));
			});
			nonEmptyCrossDims = _.filter(mappingDim.crossMapTo, (d) => crossDimObjs[d].length);
		};
		const renderAdvName = (name, isExisting) => (
			<span style={{ color: isExisting ? 'gray' : 'black' }}>{name}</span>
		);
		Base.renderGlobal((done) => (
			<PopupSelector
				forceExpanded
				fn={() => loadAdvData()}
				onApplyChanges={async ({ preventDefault }) => {
					if (!(await this.doImport({ nonExistingNames, rows, updateRowsFromAdvs, nonExistingCrossNames }))) {
						preventDefault();
					} else {
						done();
					}
				}}
				onCancel={done}
				content={() => (
					<>
						{trimProblemNames.length > 0 && (
							<Banner
								status="warning"
								message={(
									<>
										The following names in the import are already contained in the mapping in non-trimmed form. This may create duplicate looking entries.
										<ul>
											{_.map(trimProblemNames, (name) => (
												<li key={name}>{name}</li>
											))}
										</ul>
									</>
								)}
							/>
						)}
						<DataTable
							showCheckboxes={false}
							selectableRows={false}
							identifier={(row) => row.toAdv}
							definitions={[
								{
									key: 'toAdv',
									title: `Target ${mappingDim.uiName}`,
									format: (__, { fromAdvs }) => renderAdvName(fromAdvs[0].toAdv, !!fromAdvs[0].toAdvNr),
								},
								{
									key: 'toAdv',
									title: `From ${mappingDim.pluralName}`,
									format: (__, { fromAdvs }) => _.sortBy(fromAdvs).map(({ fromAdv, fromAdvNr }) => (
										<div key={fromAdv}>
											{renderAdvName(fromAdv, !!fromAdvNr)}
										</div>
									)),
								},
								...(nonEmptyCrossDims.map((d) => ({
									key: 'toAdv',
									title: `Assigned ${allDimsByName[d].uiName}`,
									format: (__, { fromAdvs }) => renderAdvName(fromAdvs[0][d], !!fromAdvs[0][`${d}Nr`]), // arbitrarily picking the first if there are multiple
								})) || []),
							]}
							data={_.sortBy(_.entries(_.groupBy(rows, 'toAdv')).map(([toAdv, fromAdvs]) => ({ toAdv, fromAdvs })), 'toAdv')}
						/>
					</>
				)}
			/>
		));
	}

	mappingDim() {
		return AllMappingDimensions.byName(this.props.model.dimType);
	}

	checkForbidden(nr, currentMapping) {
		const { advNrToMapping } = this.state;
		const existing = advNrToMapping?.[nr];
		// eslint-disable-next-line eqeqeq
		if (existing && !(currentMapping?.id == existing.id)) {
			return `Already mapped to '${this.advName(existing.targetAdvNr)}'`;
		}
		return false;
	}

	updateMappingStructures() {
		const { model } = this.props;
		const advName = (nr) => this.advName(nr);
		const options = [];
		const advNrToMapping = {};
		model.mappings?.forEach((mapping) => {
			const { targetAdvNr, byWords, advertisers, crossDimMapping } = mapping;
			const crossNames = _.filter(_.entries(crossDimMapping).filter(([type]) => seqDimsByName[type]).map(
				([type, nr]) => this.advName(nr, type),
			));
			const allAdvs = [targetAdvNr, ...(advertisers || [])];
			options.push({
				id: targetAdvNr,
				path: _.uniq([
					..._.map(allAdvs, advName),
					..._.map(byWords, 'word'),
					...crossNames,
				]).join(' '),
			});
			allAdvs.forEach((nr) => {
				advNrToMapping[nr] = mapping;
			});
		});
		this.setState({
			mappingListFilter: createFilterFnOptions(options),
			advNrToMapping,
		});
	}

	async maybeUpdatePendingField({ targetAdvNr, crossDimMapping }, dim, { current: target }) {
		if (!target) {
			return true;
		}
		const isTarg = dim === this.mappingDim();
		const current = target.getSearchString();
		if (!isTarg && !current) {
			delete (crossDimMapping || {})[dim.name];
			return true;
		}
		const oldNr = isTarg ? targetAdvNr : crossDimMapping?.[dim.name];
		const { name } = this.knownEntities[dim.name][oldNr] || {};
		if (isTarg && !current && name) {
			return prompt((
				<span>
					A target {dim.uiName} must be set, do you want to keep it as <b>{name}</b>?
				</span>
			));
		}
		if (!target.isUseNameEnabled()) {
			return true;
		}
		if (!current || current === name) {
			return true;
		}
		let question = (
			<span>
				Do you want to change the name of {isTarg ? 'target' : 'assigned'} {dim.uiName}&nbsp;
				<b>{name}</b> into <b>{current}</b>?
			</span>
		);
		if (!name) {
			question = <span>Do you want to set assigned {dim.uiName} to <b>{current}</b>?</span>;
		}
		if (!(await prompt(question))) {
			return true;
		}
		const res = await target.applyUseName();
		return res !== null; // If user don't want to create new advertiser, don't close dialog
	}

	async updateTarget({ mapping, newNr, obj, form }) {
		const { name, uiName } = this.mappingDim();
		const oldNr = mapping.targetAdvNr;
		const oldTarget = this.knownEntities[name][oldNr];
		const keep = await prompt(
			<span>
				Do you want to keep the old target {uiName} <b>{oldTarget.name}</b> as a directly mapped {uiName} to new target {uiName} <b>{obj.name}</b>?
			</span>,
		);
		form.update(() => {
			mapping.targetAdvNr = newNr;
			mapping.advertisers = _.without(mapping.advertisers, oldNr, newNr);
			if (keep) {
				mapping.advertisers.push(oldNr);
			}
		});
	}

	async updateCrossMapping({ mapping, newNr, dim, form }) {
		form.update(() => {
			if (newNr) {
				_.set(mapping, ['crossDimMapping', dim.name], newNr);
			} else {
				delete (mapping.crossDimMapping || {})[dim.name];
			}
		});
	}

	renderSingleMapping(orgMapping) {
		const { model, type, metric } = this.props;
		const { targetAdvNr } = orgMapping;
		const targetRefs = _.mapValues(seqDimsByName, () => React.createRef());
		return Base.renderGlobal((done) => (
			<FormOf
				model={_.cloneDeep(orgMapping)}
				content={({ field, model: newMapping, form }) => (
					<PopupSelector
						hideLink
						size="lg"
						form={form}
						allowErrorsIfCancel
						forceExpanded
						title={this.advName(newMapping.targetAdvNr)}
						onApplyChanges={async ({ preventDefault }) => {
							for (const [name, ref] of _.entries(targetRefs)) {
								if (!await this.maybeUpdatePendingField(newMapping, seqDimsByName[name], ref)) {
									preventDefault();
									return;
								}
							}
							const idx = _.findIndex(model.mappings, { targetAdvNr });
							model.mappings[idx] = newMapping;
							this.updateMappingStructures();
							done();
						}}
						onCancel={done}
					>
						<SingleMapping
							targetRefs={targetRefs}
							field={field}
							type={type}
							mapping={newMapping}
							checkForbidden={(nr) => this.checkForbidden(nr, newMapping)}
							metric={metric}
							knownEntities={this.knownEntities}
							mappingDim={this.mappingDim()}
							onTargetChange={async (nr, obj, dim) => {
								this.knownEntities[dim.name][nr] = obj;
								const param = { mapping: newMapping, newNr: parseInt(nr, 10), obj, form, dim };
								if (dim === this.mappingDim()) {
									await this.updateTarget(param);
								} else {
									await this.updateCrossMapping(param);
								}
							}}
						/>
					</PopupSelector>
				)}
			/>
		));
	}

	renderAddNewMapping() {
		const {
			model, model: { dimType }, type, metric,
		} = this.props;
		const mappingDim = this.mappingDim();
		const advsToNums = (arr) => arr.map((adv) => adv.advNr);

		const onAdd = ({ scope, newMappingAdvertisers }) => {
			this.knownEntities[dimType] = this.knownEntities[dimType] || {};
			newMappingAdvertisers.forEach((adv) => {
				this.knownEntities[dimType][adv.advNr] = adv;
			});
			const advertisers = newMappingAdvertisers.map((adv) => parseInt(adv.advNr, 10));
			const newMapping = new SingleAdvertiserMapping({
				targetAdvNr: advertisers[0],
				advertisers,
				byWords: [],
				tempRandom: Math.random(),
				dimType: mappingDim.name,
			});
			model.mappings.push(newMapping);
			scope.setState({ newMappingAdvertisers: [] });
			if (this.advList) {
				this.advList.setSearchString('');
			}
			this.updateMappingStructures();
			this.renderSingleMapping(newMapping);
		};

		return (
			<Scope
				content={(scope, { newMappingAdvertisers = [] }) => (
					<Card style={{ overflow: 'visible' }}>
						<CardContent>
							<Typography variant="h2">
								Add new mapping from target
								{' '}
								{mappingDim.name}
							</Typography>
							<DynamicSearchFilter
								selected={advsToNums(newMappingAdvertisers)}
								onChange={(__, advs) => scope.setState({ newMappingAdvertisers: advs })}
								type={type}
								serverCall={() => newMappingAdvertisers}
								createUsingDimObject={mappingDim}
								hideEmptySelected
								ref={(advList) => {
									this.advList = advList;
								}}
								metric={metric}
								dimension="advNr"
								label={mappingDim.dimension}
								groupBy={mappingDim.dimension}
								noMapping
								fullWidth
								margin="normal"
								textFieldLabel={`Add ${mappingDim.uiCName}(s)`}
								showMoreLabel={mappingDim.pluralName}
								checkForbidden={(nr) => this.checkForbidden(nr)}
								noObjectCreate={mappingDim.sysSpecific}
							/>
							<ActionButton
								label="Add"
								disabled={newMappingAdvertisers.length === 0}
								onClick={() => onAdd({ scope, newMappingAdvertisers })}
							/>
						</CardContent>
					</Card>
				)}
			/>
		);
	}

	async exportCsv(perMappedAdv, doneFn, { includeTargetExtId, includeSsps, advReportStart, includeNoOpMappings }) {
		const mappingDim = this.mappingDim();
		const { model, model: { dimType } } = this.props;
		const mappings = _.sortBy(model.mappings, (m) => this.advName(m.targetAdvNr));
		const rows = [];
		let advToSsp;
		if (includeSsps) {
			const { dimension, reportProp } = mappingDim;
			const { data, labels } = await Reports.reportProgrammatic({
				groupBy: [dimension, 'sspId'],
				sums: ['soldImpressions'],
				maxAdvertisers: 100000,
				start: advReportStart,
				end: DateUtils.yesterday(),
				[reportProp]: SystemData.genericData.ADVERTISERS.DUMMY_NO_MAPPING,
			});
			advToSsp = _.mapValues(_.invert(labels[dimension]), (nr) => (
				_.keys(data[nr]).map((sspId) => labels.sspId[sspId])
			));
		}
		const escapeCommas = (str) => str.replace(/\\/g, '\\\\').replace(/,/g, '\\,');
		mappings.forEach((m) => {
			const { externalId = '', name: target = '' } = this.knownEntities[dimType][m.targetAdvNr] || {};
			const advs = m.advertisers?.map((a) => this.advName(a)).filter((a) => a && a !== target);
			if (advToSsp?.[target]) { // include target advertiser when there are SSPs using that name
				advs.push(target);
			}
			const words = m.byWords.map((w) => w?.word).filter((w) => w);
			if (!target || (!(advs.length || words.length || m.crossDimMapping) && !includeNoOpMappings)) {
				return;
			}
			const base = { target, externalId };
			const crossDims = _.merge({}, _.mapValues(_.keyBy(mappingDim.crossMapTo), () => ''), _.mapValues(m.crossDimMapping, (nr, type) => this.advName(nr, type)));
			if (perMappedAdv) {
				rows.push(...advs.map((adv) => (
					(advToSsp?.[adv] || ['']).map((ssp) => ({ ...base, adv: escapeCommas(adv), ssp, ...crossDims }))
				)).flat());
				if (!advs.length) { // if row has no direct mapping but has other stuff
					rows.push({ ...base, adv: '', ssp: '', ...crossDims });
				}
			} else {
				rows.push(({ ...base, advs: advs.map(escapeCommas).join(', '), words: words.join(', '), ...crossDims }));
			}
		});
		const COLS = {
			target: `Target ${mappingDim.uiName}`,
			...(includeTargetExtId && { externalId: `Target ${mappingDim.uiName} External ID` }),
			...(perMappedAdv ? {
				adv: `Mapped ${mappingDim.uiName}`,
			} : {
				advs: 'Direct mappings',
				words: 'Match by words', // TODO why is this not in perMappedAdv as well?
			}),
			...(includeSsps && { ssp: 'Source SSP' }),
			...(_.fromPairs(mappingDim.crossMapTo?.map((c) => [c, `Assigned ${allDimsByName[c].uiName}`]))),
		};
		BrowserUtils.downloadTextFile(createCsv(rows, COLS), `${model.name || 'mapping'}.csv`);
		doneFn();
	}

	crossMappingSummaryCols() {
		const { model } = this.props;
		const types = _.uniq(model.mappings.map((m) => _.keys(m.crossDimMapping)).flat());
		if (!types.length) {
			return [];
		} else if (types.length <= MAX_CROSS_COLS) { // One column per type
			return this.mappingDim().crossDims.filter(({ name }) => types.includes(name)).map(({ name, uiName }) => ({
				key: 'crossDimMapping',
				title: `Assigned ${uiName}`,
				format: (obj) => this.knownEntities[name][obj?.[name]]?.name || '',
			}));
		}
		const format = (obj) => ( // All types in same cell
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{_.entries(_.pick(obj, _.keys(allDimsByName))).map(([name, val]) => (
					<div key={name}>
						<b>{allDimsByName[name].uiCName}</b>: {this.knownEntities[name]?.[val]?.name || '?'}
					</div>
				))}
			</div>
		);
		return [{ key: 'crossDimMapping', title: 'Auto assignments', format }];
	}

	renderExportToCsv() {
		const mappingDim = this.mappingDim();
		Base.renderGlobal((close) => (
			<MuiDialog open onClose={close}>
				<DialogTitle>CSV Export</DialogTitle>
				<DialogContent dividers>
					Do you want to export mappings one line per target {mappingDim.name} (as in the list), or one line
					for each mapped {mappingDim.name}.
				</DialogContent>
				<DialogActions>
					<FormOf
						model={{
							includeTargetExtId: false,
							includeSsps: false,
							advReportStart: DateUtils.daysFromToday(-30),
						}}
						content={({ field, model }) => (
							<Stack spacing={2}>
								<Checkbox
									label={`Include External ID for target ${mappingDim.uiName}`}
									fullWidth
									{...field('includeTargetExtId')}
								/>
								<Checkbox
									label="Include source SSPs from report data"
									fullWidth
									{...field('includeSsps')}
								/>
								<Checkbox
									label="Include mappings with no settings except of Target advertiser"
									fullWidth
									{...field('includeNoOpMappings')}
								/>
								{model.includeSsps && (
									<DatePicker
										floatingLabelText="Read report data for SSPs from"
										maxDate={DateUtils.yesterday()}
										autoOk
										{...field('advReportStart')}
									/>
								)}
								<Stack spacing={10} direction="row">
									<JobButton
										disabled={model.includeSsps}
										label={`Per target ${mappingDim.uiName}`}
										fn={() => this.exportCsv(false, close, model)}
									/>
									<JobButton
										label={`Per mapped ${mappingDim.uiName}`}
										fn={() => this.exportCsv(true, close, model)}
									/>
									<Button onClick={close} variant="text">Cancel</Button>
								</Stack>
							</Stack>
						)}
					/>
				</DialogActions>
			</MuiDialog>
		));
	}

	render() {
		const {
			model, submit, field, onCancel, all, type, metric,
		} = this.props;
		const { mappingListFilterStr } = this.state;
		const mappingDim = this.mappingDim();
		const advName = (nr) => this.advName(nr);
		const isAdmin = stores.identity.isAdministrator();
		const mappingIdx = (mapping) => model.mappings.indexOf(mapping);
		return (
			<OperationWrapper
				fn={() => this.loadAdvertisers()}
				style={{ width: '100%' }}
				content={() => (
					<Grid container spacing={3}>
						<Grid item xs={4}>
							<Grid container spacing={3} justifyContent="flex-end">
								<Grid item xs={12}>
									<Card>
										<CardContent>
											<Typography variant="h2">
												General settings
											</Typography>
											<TextField label="Name" required {...field('name')} fullWidth margin="normal" />
											<Select
												label="Parent Mapping"
												{...field('parent')}
												nonSelected="(none)"
												items={_.sortBy(all.filter((m) => (
													m.dimType === mappingDim.name && m.id !== model.id
												)), 'name').map((m) => ({ label: m.name, value: m.id }))}
												fullWidth
												margin="normal"
											/>
											{isAdmin && <Checkbox label="User visible" {...field('userVisible')} margin="normal" />}
										</CardContent>
									</Card>
								</Grid>
								<Grid item xs={12}>
									{this.renderAddNewMapping()}
								</Grid>
								<Grid item>
									<ActionButton
										label="Cancel"
										onClick={onCancel}
										variant="outlined"
										color="primary"
									/>
								</Grid>
								<Grid item>
									<ActionButton
										label="Save changes"
										color="primary"
										onClick={submit}
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={8}>
							<Card>
								<CardContent>
									<div className="flex items-center justify-between">
										<Typography variant="h2">
											Mappings
										</Typography>
										<div className="flex items-center gap-2">
											<ActionButton
												label="Download to .csv"
												icon={<DownloadIcon />}
												useIconButton
												onClick={() => this.renderExportToCsv()}
												color="primary"
											/>
											{this.props.canCreateAdvertisers && (
												<CsvImport
													colSelectors={[
														{
															name: 'toAdv',
															label: `Target ${mappingDim.uiName} colum`,
															required: true,
														},
														{
															name: 'fromAdv',
															label: `From ${mappingDim.uiName} column`,
															required: true,
														},
														...(mappingDim.crossMapTo?.map((dim) => ({
															name: dim,
															label: `Assigned ${allDimsByName[dim].uiName}`,
															required: false,
														})) || []),
													]}
													onCsvImported={(rows) => { this.handleCsvImport(rows); }}
													buttonLabel="Upload from .csv"
													useIcon
												/>
											)}
											<SmartHelp
												query={`${mappingDim.pluralCname} mappings`}
												question={`Guide the user on how to use the ${mappingDim.name} mappings feature in detail`}
												showFollowUpSuggestion
											/>
										</div>
									</div>
									<Grid container spacing={3}>
										<Grid item xs={12}>
											<TextField
												label="Filter mappings"
												uncontrolled
												defaultValue={mappingListFilterStr}
												name="filterMappings"
												startAdornment={<FilterAltOutlinedIcon />}
												onChange={(ev) => {
													this.lastFilterUpdateEvent = ev;
													const setOpacity = (val) => {
														const { style } = this.mappingListRef.current;
														if (style) {
															style.opacity = val;
														}
													};
													setOpacity(0.4);
													setTimeout(() => {
														if (this.lastFilterUpdateEvent === ev) {
															this.setState({ mappingListFilterStr: ev.target.value });
															setOpacity(1);
														}
													}, 500);
												}}
												fullWidth
												margin="normal"
											/>
										</Grid>
										<Grid item xs={12} ref={this.mappingListRef}>
											<DataGrid
												pagination
												disableColumnMenu
												disableSelectionOnClick
												identifier={(row) => row.id || row.tempRandom}
												definitions={[
													{
														key: 'targetAdvNr',
														title: `Target ${mappingDim.uiName}`,
														sortable: true,
														transform: (nr) => advName(nr),
													},
													{
														key: 'advertisers',
														title: 'Direct mappings',
														flex: 1.3,
														sortable: true,
														transform: (advertisers, mapping) => (advertisers || [])
															.filter((nr) => nr !== mapping.targetAdvNr)
															.map(advName)
															.join(', '),
													},
													{
														key: 'byWords',
														title: 'Match by words',
														sortable: true,
														transform: (byWords) => (byWords || []).map((by) => by.word).join(', '),
													},
													...this.crossMappingSummaryCols(),
													{
														key: 'targetAdvNr',
														title: 'Matchings',
														minWidth: 100,
														flex: 0.3,
														format: (__, mapping) => (
															<MatchReport
																type={type}
																metric={metric}
																mapping={{ mappings: [mapping], dimType: mappingDim.name }}
																dimType={mappingDim.name}
															/>
														),
													},
													{
														key: 'targetAdvNr',
														minWidth: 100,
														flex: 0.3,
														title: '',
														format: (__, mapping) => (
															<RecordTableTools
																editAction={() => this.renderSingleMapping(mapping)}
																deleteAction={() => {
																	model.mappings.splice(mappingIdx(mapping), 1);
																	this.updateMappingStructures();
																}}
															/>
														),
													},
												]}
												data={this.getVisibleMappings()}
											/>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				)}
			/>
		);
	}
}

AdvertiserMappingEditor.propTypes = {
	model: PropTypes.object.isRequired,
	form: PropTypes.object.isRequired,
	all: PropTypes.array.isRequired,
	canCreateAdvertisers: PropTypes.bool.isRequired,
	field: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	submit: PropTypes.func.isRequired,
	type: PropTypes.string,
	metric: PropTypes.string,
};

AdvertiserMappingEditor.defaultProps = {
	type: 'programmatic',
	metric: 'revenue',
};

export default AdvertiserMappingEditor;
